import { AuthService } from '@teko/auth';
import { UserService } from '@teko/user';
import { DiscoveryService } from '@teko/discovery';
import { CartService } from '@teko/cart';
import { LocationService } from '@teko/location';
import { OrderService } from '@teko/order';
import { NotificationService } from '@teko/notification';
import { WarehouseService } from '@teko/warehouse';
import { TerraService } from '@teko/terra-bff';
import { PaymentService } from '@teko/payment';
import { LoyaltyService } from '@teko/loyalty';
import { TenantEnv } from 'types';

export class ServiceContainer {
  private _authService: AuthService;
  private _userService: UserService;
  private _discoveryService: DiscoveryService;
  private _discoveryPos365Service: DiscoveryService;
  private _cartService: CartService;
  private _locationService: LocationService;
  private _orderService: OrderService;
  private _notificationService: NotificationService;
  private _warehouseService: WarehouseService;
  private _terraService: TerraService;
  private _paymentService: PaymentService;
  private _loyaltyService: LoyaltyService;

  private tenantConfig: TenantEnv;
  constructor(tenantConfig: TenantEnv) {
    this.tenantConfig = tenantConfig;
  }

  get authService() {
    if (!this._authService) {
      this._authService = new AuthService(
        this.tenantConfig.getProcessEnv('IAM_API_URL'),
        this.tenantConfig.iamClientId ||
          this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID'),
        this.tenantConfig.iamOauthDomain ||
          this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_OAUTH_DOMAIN'),
      );
    }
    return this._authService;
  }

  get userService() {
    if (!this._userService) {
      this._userService = new UserService(
        this.tenantConfig.getProcessEnv('NEXT_PUBLIC_BASE_USER_SERVICE_URL'),
        this.tenantConfig.platformCode,
        this.tenantConfig.platformId,
      );
    }
    return this._userService;
  }

  get discoveryService() {
    if (!this._discoveryService) {
      const publicDiscoveryEndpoint =
        this.tenantConfig.getProcessEnv('BASE_DISCOVERY_URL');
      const internalDiscoveryEndpoint = this.tenantConfig.getProcessEnv(
        'INTERNAL_DISCOVERY_URL',
      );
      // use internal discovery endpoint
      // for server side rendering
      this._discoveryService = new DiscoveryService(
        typeof window === 'undefined' && internalDiscoveryEndpoint
          ? internalDiscoveryEndpoint
          : publicDiscoveryEndpoint,
        undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryService;
  }

  get discoveryPos365Service() {
    if (!this._discoveryPos365Service) {
      this._discoveryPos365Service = new DiscoveryService(
        this.tenantConfig.getProcessEnv('BASE_POS365_DISCOVERY_URL'),
        undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryPos365Service;
  }

  get cartService() {
    if (!this._cartService) {
      this._cartService = new CartService(
        this.tenantConfig.getProcessEnv('BASE_CART_API_URL'),
        undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._cartService;
  }

  get locationService() {
    if (!this._locationService) {
      this._locationService = new LocationService(
        this.tenantConfig.getProcessEnv('BASE_LOCATION_URL'),
      );
    }
    return this._locationService;
  }

  get orderService() {
    if (!this._orderService) {
      this._orderService = new OrderService(
        this.tenantConfig.getProcessEnv('BASE_COV_URL'),
      );
    }
    return this._orderService;
  }

  get notificationService() {
    if (!this._notificationService) {
      this._notificationService = new NotificationService(
        this.tenantConfig.getProcessEnv('BASE_NOTIFICATIONS_API'),
      );
    }
    return this._notificationService;
  }

  get warehouseService() {
    if (!this._warehouseService) {
      this._warehouseService = new WarehouseService(
        this.tenantConfig.getProcessEnv('BASE_WAREHOUSE_CONSUMER_API_URL'),
      );
    }
    return this._warehouseService;
  }

  get terraService(): TerraService {
    if (!this._terraService) {
      this._terraService = new TerraService(
        this.tenantConfig.getProcessEnv('BASE_TERRA_BFF_API_URL'),
      );
    }
    return this._terraService;
  }

  get paymentService(): PaymentService {
    if (!this._paymentService) {
      this._paymentService = new PaymentService(
        this.tenantConfig.getProcessEnv('BASE_PAYMENT_API_URL'),
        this.tenantConfig.paymentSecretKey ||
          this.tenantConfig.getProcessEnv('PAYMENT_SECRET_KEY'),
        this.tenantConfig.getProcessEnv('BASE_MAIN_PAYMENT_API_URL'),
      );
    }
    return this._paymentService;
  }

  get loyaltyService(): LoyaltyService {
    if (!this._loyaltyService) {
      this._loyaltyService = new LoyaltyService(
        this.tenantConfig.getProcessEnv('BASE_LOYALTY_API_URL'),
        this.tenantConfig.getProcessEnv('LOYALTY_CLIENT_ID'),
      );
    }
    return this._loyaltyService;
  }
}
